import { useEffect, useState } from "react";
import { ArrowRight, BookOpen, CheckCircle2, Clock, Layers } from "lucide-react";
import { Link } from "react-router-dom";

import { SelfCourse, selfCoursesApi } from "../api/selfCourses";

const getStats = (course: SelfCourse) => {
  const lessons = course.modules.flatMap((module) => module.lessons);
  const minutes = lessons.reduce(
    (sum, lesson) => sum + Number(lesson.durationMinutes || 0),
    0
  );
  return { lessons: lessons.length, hours: Math.ceil(minutes / 60) };
};

const SelfCoursesPage = () => {
  const [courses, setCourses] = useState<SelfCourse[]>([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");

  useEffect(() => {
    selfCoursesApi
      .listMine()
      .then(setCourses)
      .catch(() => setMessage("Не вдалося завантажити курси."))
      .finally(() => setLoading(false));
  }, []);

  return (
    <section className="min-h-full bg-[#f4f7fb] px-4 pb-6 pt-6 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-[1320px]">
        <div className="mb-6 border-b border-[#E0DCD5] pb-5">
          <p className="text-sm font-black uppercase tracking-[0.14em] text-[#8a6f00]">
            Самостійне навчання
          </p>
          <div className="mt-1 flex flex-col justify-between gap-3 lg:flex-row lg:items-end">
            <div>
              <h1 className="text-3xl font-black uppercase leading-none tracking-[-0.05em] text-black sm:text-5xl">
                Мої курси
              </h1>
              <p className="mt-3 max-w-2xl text-sm font-semibold leading-6 text-[#777068]">
                Проходь модулі у своєму темпі: дивись матеріали, виконуй тести
                та здавай практичні роботи посиланням.
              </p>
            </div>
          </div>
        </div>

        {message && (
          <div className="mb-5 rounded-xl border border-[#E5D2C5] bg-[#FFF8F3] px-4 py-3 text-sm font-semibold text-[#8A4A2E]">
            {message}
          </div>
        )}

        {loading ? (
          <div className="rounded-3xl border border-[#E3DED6] bg-white p-8 text-sm font-bold text-[#817A72]">
            Завантажуємо курси...
          </div>
        ) : (
          <div className="grid gap-5 lg:grid-cols-2 xl:grid-cols-3">
            {courses.map((course) => {
              const stats = getStats(course);
              const progress = course.enrollment?.progress || 0;

              return (
                <article
                  key={course._id}
                  className="flex min-h-[360px] flex-col overflow-hidden rounded-[28px] border-2 border-black bg-white shadow-[7px_7px_0_#ffd400]"
                >
                  <div className="relative border-b-2 border-black bg-[#111] p-6 text-white">
                    <div className="absolute right-5 top-5 h-20 w-28 bg-[repeating-linear-gradient(135deg,rgba(255,212,0,0.35)_0_2px,transparent_2px_12px)]" />
                    <span className="inline-flex rounded-full border border-[#ffd400] bg-[#ffd400] px-3 py-1 text-xs font-black uppercase text-black">
                      {course.level || "Базовий"}
                    </span>
                    <h2 className="relative mt-5 max-w-[360px] text-3xl font-black uppercase leading-none tracking-[-0.05em]">
                      {course.title}
                    </h2>
                    <p className="relative mt-4 line-clamp-3 text-sm font-semibold leading-6 text-white/75">
                      {course.description}
                    </p>
                  </div>

                  <div className="flex flex-1 flex-col p-5">
                    <div className="grid grid-cols-3 gap-2 text-xs font-black text-[#625C55]">
                      <span className="inline-flex items-center gap-1 rounded-xl bg-[#F7F4EF] px-3 py-2">
                        <Layers size={14} /> {course.modules.length} мод.
                      </span>
                      <span className="inline-flex items-center gap-1 rounded-xl bg-[#F7F4EF] px-3 py-2">
                        <BookOpen size={14} /> {stats.lessons}
                      </span>
                      <span className="inline-flex items-center gap-1 rounded-xl bg-[#F7F4EF] px-3 py-2">
                        <Clock size={14} /> {stats.hours} год.
                      </span>
                    </div>

                    <div className="mt-5">
                      <div className="flex items-center justify-between text-sm">
                        <span className="font-black text-black">Прогрес</span>
                        <span className="font-black text-[#8a6f00]">
                          {progress}%
                        </span>
                      </div>
                      <div className="mt-2 h-3 overflow-hidden rounded-full border border-black/10 bg-[#EEEAE4]">
                        <div
                          className="h-full rounded-full bg-[#ffd400]"
                          style={{ width: `${progress}%` }}
                        />
                      </div>
                    </div>

                    <Link
                      to={`/self-courses/${course._id}/learn`}
                      className="mt-auto inline-flex h-12 items-center justify-center gap-2 rounded-2xl border-2 border-black bg-[#ffd400] px-5 text-sm font-black text-black shadow-[4px_4px_0_#000] transition hover:-translate-y-0.5"
                    >
                      {progress > 0 ? "Продовжити" : "Почати курс"}
                      {progress >= 100 ? <CheckCircle2 size={18} /> : <ArrowRight size={18} />}
                    </Link>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
};

export default SelfCoursesPage;
